import { FC, useMemo, useState } from "react";
import { toast } from "react-toastify";

import Button from "../../../../components/Button";
import ModalForm, { Field, FieldType } from "../../../../components/ModalForm";
import Text from "../../../../components/Text";
import RepeatAssignConfirm from "../RepeatAssignConfirm";
import {
    assignUsersToPromotion,
    getPromotionOptions,
    readRepeatConfirmationUserIds,
    AssignPromotionResult,
    Promotion,
} from "../../../../services/api/admin/promotions";
import { resolveUsersByEmails } from "../../../../services/api/admin/bulkUsers";
import {
    extractEmailsFromCsvColumn,
    guessEmailColumn,
    parseCsv,
} from "../../../../utils/csv";
import { CSV_FILE_ACCEPT } from "../../../../utils/fileAccept";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
    notifyFieldErrors,
    setFieldErrors,
} from "../../../../utils/errors";
import styles from "./styles.module.scss";

type Step = "file" | "promo";

type Props = {
    open: boolean;
    handleClose: () => void;
    onCompleted?: () => void;
};

const downloadIgnoredEmailsCsv = (emails: string[]) => {
    const csv =
        "\uFEFFemail\n" +
        emails.map((email) => `"${email.replace(/"/g, '""')}"`).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
        "download",
        `emails_ignores_${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const ImportAssignPromoModal: FC<Props> = ({
    open,
    handleClose,
    onCompleted,
}) => {
    const [step, setStep] = useState<Step>("file");
    const [file, setFile] = useState<File | undefined>();
    const [headers, setHeaders] = useState<string[]>([]);
    const [rows, setRows] = useState<string[][]>([]);
    const [emailColumn, setEmailColumn] = useState("");
    const [matchedUserIds, setMatchedUserIds] = useState<string[]>([]);
    const [ignoredEmails, setIgnoredEmails] = useState<string[]>([]);
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [promotionId, setPromotionId] = useState("");
    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrorsState] = useState<FieldErrors>({});
    const [repeatPrompt, setRepeatPrompt] = useState<{
        count: number;
        resolve: (result: AssignPromotionResult | undefined) => void;
        reject: (error: unknown) => void;
    } | null>(null);
    const [repeatLoading, setRepeatLoading] = useState(false);

    const ignoredCount = ignoredEmails.length;

    const resetState = () => {
        setStep("file");
        setFile(undefined);
        setHeaders([]);
        setRows([]);
        setEmailColumn("");
        setMatchedUserIds([]);
        setIgnoredEmails([]);
        setPromotionId("");
        setPromotions([]);
        setFieldErrorsState({});
    };

    const closeAndReset = () => {
        resetState();
        handleClose();
    };

    const emailPreviewCount = useMemo(() => {
        if (!emailColumn || rows.length === 0) return 0;
        return extractEmailsFromCsvColumn(headers, rows, emailColumn).length;
    }, [emailColumn, headers, rows]);

    const handleFileChange = async (nextFile: File | undefined) => {
        setFile(nextFile);
        setFieldErrorsState((prev) => clearFieldError(prev, "csv"));
        setHeaders([]);
        setRows([]);
        setEmailColumn("");
        setIgnoredEmails([]);
        setMatchedUserIds([]);
        if (!nextFile) return;

        try {
            const text = await nextFile.text();
            const parsed = parseCsv(text);
            if (parsed.headers.length === 0) {
                toast.error("Le fichier CSV est vide ou invalide");
                return;
            }
            setHeaders(parsed.headers);
            setRows(parsed.rows);
            setEmailColumn(guessEmailColumn(parsed.headers));
        } catch {
            toast.error("Impossible de lire le fichier CSV");
        }
    };

    const handleResolve = async () => {
        const nextErrors = setFieldErrors(
            ["csv", !file && REQUIRED_FIELD],
            ["emailColumn", !!file && !emailColumn && REQUIRED_FIELD]
        );
        setFieldErrorsState(nextErrors);
        if (
            notifyFieldErrors(nextErrors, {
                message: "Veuillez choisir un fichier et une colonne email",
                type: "error",
            })
        ) {
            return;
        }
        const extracted = extractEmailsFromCsvColumn(
            headers,
            rows,
            emailColumn
        );
        if (extracted.length === 0) {
            toast.error("Aucun email trouvé dans la colonne sélectionnée");
            return;
        }

        setLoading(true);
        try {
            const [result, promos] = await Promise.all([
                resolveUsersByEmails(extracted),
                getPromotionOptions(),
            ]);
            if (!result) {
                toast.error("Erreur lors du matching des emails");
                return;
            }

            setMatchedUserIds(result.matched.map((u) => u.id));
            setIgnoredEmails(result.ignoredEmails ?? []);
            setPromotions(promos ?? []);

            if (result.matched.length === 0) {
                toast.warning(
                    `Aucun utilisateur trouvé (${result.ignoredCount} adresse(s) ignorée(s))`
                );
                return;
            }

            setStep("promo");
        } catch {
            toast.error("Erreur lors du matching des emails");
        } finally {
            setLoading(false);
        }
    };

    const finishRepeatChoice = async (countAsRepeat: boolean) => {
        if (!repeatPrompt) return;
        setRepeatLoading(true);
        try {
            const result = await assignUsersToPromotion(
                promotionId,
                matchedUserIds,
                countAsRepeat
            );
            repeatPrompt.resolve(result);
            setRepeatPrompt(null);
        } catch (error) {
            repeatPrompt.reject(error);
            setRepeatPrompt(null);
        } finally {
            setRepeatLoading(false);
        }
    };

    const handleAssign = async () => {
        const nextErrors = setFieldErrors([
            "promotionId",
            !promotionId && REQUIRED_FIELD,
        ]);
        setFieldErrorsState(nextErrors);
        if (
            notifyFieldErrors(nextErrors, {
                message: "Veuillez sélectionner une promotion",
                type: "error",
            })
        ) {
            return;
        }
        if (matchedUserIds.length === 0) {
            toast.error("Aucun utilisateur à attribuer");
            return;
        }

        setLoading(true);
        try {
            const result = await new Promise<
                AssignPromotionResult | undefined
            >((resolve, reject) => {
                const attempt = async (countAsRepeat?: boolean) => {
                    try {
                        resolve(
                            await assignUsersToPromotion(
                                promotionId,
                                matchedUserIds,
                                countAsRepeat
                            )
                        );
                    } catch (error) {
                        const changingIds = readRepeatConfirmationUserIds(error);
                        if (!changingIds || countAsRepeat !== undefined) {
                            reject(error);
                            return;
                        }
                        setRepeatPrompt({
                            count: changingIds.length,
                            resolve,
                            reject,
                        });
                    }
                };
                void attempt();
            });
            if (!result) return;
            const promoted = result.promotedToJunior ?? 0;
            const repeats = result.repeatsRecorded ?? 0;
            toast.success(
                [
                    `${result.updated ?? matchedUserIds.length} apprenant(s) attribués`,
                    promoted > 0
                        ? `${promoted} passé${promoted > 1 ? "s" : ""} Junior`
                        : null,
                    repeats > 0
                        ? `${repeats} redoublement${repeats > 1 ? "s" : ""}`
                        : null,
                ]
                    .filter(Boolean)
                    .join(" · ")
            );
            onCompleted?.();
            closeAndReset();
        } catch {
            toast.error("Échec de l'attribution");
        } finally {
            setLoading(false);
        }
    };

    const ignoredExportBlock =
        ignoredCount > 0 ? (
            <div className={styles.ignored_export}>
                <Text variant="body" className={styles.preview}>
                    {ignoredCount} adresse
                    {ignoredCount > 1 ? "s" : ""} ignorée
                    {ignoredCount > 1 ? "s" : ""}
                </Text>
                <Button
                    size="small"
                    variant="outlined"
                    label="Télécharger les emails ignorés (CSV)"
                    onClick={() => downloadIgnoredEmailsCsv(ignoredEmails)}
                />
            </div>
        ) : null;

    const fileFields: Field<FieldType>[] = [
        {
            id: "csv",
            label: "Fichier CSV",
            type: "file",
            accept: CSV_FILE_ACCEPT,
            value: file,
            onChange: handleFileChange,
            error: fieldErrors.csv,
        },
        ...(headers.length > 0
            ? ([
                  {
                      id: "emailColumn",
                      label: "Colonne email",
                      type: "select",
                      value: emailColumn,
                      onChange: (value: string) => {
                          setEmailColumn(value);
                          setFieldErrorsState((prev) =>
                              clearFieldError(prev, "emailColumn")
                          );
                      },
                      error: fieldErrors.emailColumn,
                      options: headers.map((header) => ({
                          value: header,
                          label: header,
                      })),
                  },
              ] as Field<FieldType>[])
            : []),
    ];

    const promoFields: Field<FieldType>[] = [
        {
            id: "promotionId",
            label: "Promotion à attribuer",
            type: "select",
            value: promotionId,
            onChange: (value: string) => {
                setPromotionId(value);
                setFieldErrorsState((prev) =>
                    clearFieldError(prev, "promotionId")
                );
            },
            error: fieldErrors.promotionId,
            options: [
                { value: "", label: "Choisir…" },
                ...promotions.map((p) => ({
                    value: p.id,
                    label: `${p.name} (${p.usersCount})`,
                })),
            ],
        },
    ];

    if (step === "file") {
        return (
            <ModalForm
                open={open}
                handleClose={closeAndReset}
                title="Import CSV · Attribution promo"
                submitLabel="Continuer"
                onSubmit={() => {
                    void handleResolve();
                }}
                isLoading={loading}
                size="medium"
                fields={fileFields}
                customContent={
                    <>
                        {emailColumn && emailPreviewCount > 0 && (
                            <Text variant="body" className={styles.preview}>
                                {emailPreviewCount} email
                                {emailPreviewCount > 1 ? "s" : ""} détecté
                                {emailPreviewCount > 1 ? "s" : ""}
                            </Text>
                        )}
                        {ignoredExportBlock}
                    </>
                }
            />
        );
    }

    return (
        <>
        <RepeatAssignConfirm
            open={repeatPrompt !== null}
            count={repeatPrompt?.count ?? 0}
            loading={repeatLoading}
            onClose={() => {
                if (repeatLoading) return;
                repeatPrompt?.resolve(undefined);
                setRepeatPrompt(null);
            }}
            onNo={() => {
                void finishRepeatChoice(false);
            }}
            onYes={() => {
                void finishRepeatChoice(true);
            }}
        />
        <ModalForm
            open={open}
            handleClose={closeAndReset}
            title="Import CSV · Attribution promo"
            submitLabel="Attribuer"
            onSubmit={() => {
                void handleAssign();
            }}
            isLoading={loading}
            size="medium"
            fields={promoFields}
            customContent={
                <>
                    <Text variant="body" className={styles.preview}>
                        {matchedUserIds.length} utilisateur
                        {matchedUserIds.length > 1 ? "s" : ""} trouvé
                        {matchedUserIds.length > 1 ? "s" : ""}
                    </Text>
                    {ignoredExportBlock}
                    <Button
                        size="small"
                        variant="outlined"
                        label="Retour"
                        onClick={() => setStep("file")}
                    />
                </>
            }
        />
        </>
    );
};

export default ImportAssignPromoModal;
